/**
 * Language, direction, timezone and formatting for the open organization.
 *
 * The one place that turns "who is signed in" and "which organization is
 * open" into what the interface looks like:
 *
 *   language   the person's choice  -> organization default -> English
 *   timezone   the person's choice  -> organization timezone
 *   bilingual  organization setting (Language & Display)
 *   formats    organization: currency, locale, date and time format
 *
 * and applies it to i18next (i18n/index.js), to <html lang dir>, and to the
 * formatting helpers (lib/format.js). Screens never do any of this
 * themselves; they call t() and the format helpers.
 *
 * Mounted inside TenantProvider (layouts/AppLayout.jsx), because it needs the
 * organization's settings.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext.jsx';
import { useTenant } from './TenantContext.jsx';
import { authApi } from '../services/index.js';
import i18n, { setBilingual, setLanguage as applyLanguage } from '../i18n/index.js';
import { directionOf, resolveLanguage, resolveTimeZone, secondaryLanguageFor } from '../i18n/languages.js';
import { configureFormatting } from '../lib/format.js';

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const { user, setUser } = useAuth();
  const { settings, localizationKnown } = useTenant();

  const language = resolveLanguage({
    userLanguage: user?.preferredLanguage,
    organizationLanguage: settings.defaultLanguage,
  });
  const timeZone = resolveTimeZone({
    userTimeZone: user?.preferredTimezone,
    organizationTimeZone: settings.timezone,
  });
  const bilingual = Boolean(settings.bilingualMode);

  // Configured DURING render rather than in an effect, so the very first
  // render of every screen below formats with the organization's settings.
  // Idempotent: configureFormatting only bumps its revision on a real change.
  const revision = configureFormatting({
    language,
    locale: settings.locale,
    currency: settings.currency,
    dateFormat: settings.dateFormat,
    timeFormat: settings.timeFormat,
    firstDayOfWeek: settings.firstDayOfWeek,
    timeZone,
    organizationTimeZone: settings.timezone,
    userTimeZone: user?.preferredTimezone ?? undefined,
  });

  // i18next switches asynchronously: the new language's files load first, and
  // only then does every translated component re-render — so there is no
  // flash of the old language, and no reload or sign-out.
  const [applied, setApplied] = useState(() => i18n.language === language);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await applyLanguage(language);
      await setBilingual(bilingual);
      if (!cancelled) setApplied(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [language, bilingual]);

  /**
   * The person changes their OWN language or timezone. Saved to their
   * profile, so it follows them to every browser and survives sign-out; the
   * interface switches as soon as the server confirms. `null` goes back to
   * the organization's default.
   */
  const savePreferences = useCallback(
    async (patch) => {
      const saved = await authApi.updatePreferences(patch);
      setUser((current) =>
        current
          ? {
              ...current,
              preferredLanguage: saved.preferredLanguage ?? null,
              preferredTimezone: saved.preferredTimezone ?? null,
            }
          : current
      );
      return saved;
    },
    [setUser]
  );

  const value = useMemo(
    () => ({
      language,
      direction: directionOf(language),
      isRTL: directionOf(language) === 'rtl',
      bilingual,
      secondaryLanguage: bilingual ? secondaryLanguageFor(language) : null,
      timeZone,
      organizationTimeZone: settings.timezone,
      userLanguage: user?.preferredLanguage ?? null,
      userTimeZone: user?.preferredTimezone ?? null,
      organizationLanguage: settings.defaultLanguage ?? 'en',
      /**
       * Changes whenever anything that affects formatted output changes. The
       * shell keys the routed page on it, so screens that only format (and so
       * never subscribe to anything) still re-render in the new format.
       */
      revision,
      /** False until the organization's localization is known and applied. */
      ready: localizationKnown && applied,
      savePreferences,
    }),
    [
      language,
      bilingual,
      timeZone,
      settings.timezone,
      settings.defaultLanguage,
      user?.preferredLanguage,
      user?.preferredTimezone,
      revision,
      localizationKnown,
      applied,
      savePreferences,
    ]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/** The active language, direction, timezone and the person's own preferences. */
export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside a LocaleProvider');
  return context;
}

/** Outside an organization (sign-in, public portals): null instead of throwing. */
export function useOptionalLocale() {
  return useContext(LocaleContext);
}
